import { Reveal } from "@/components/Reveal";
import { Volume2, VolumeX } from "lucide-react";
import { useRef, useState } from "react";

const machines = [
  {
    key: "si180",
    src: "/factory/si180-front.jpg",
    label: "TOYO Si-180-6s",
    meta: "180 Ton · All-electric",
    span: "lg:col-span-4",
  },
  {
    key: "mould",
    src: "/factory/si180-mould.webp",
    label: "4-Cavity Mould",
    meta: "500 ml Rectangular · PP",
    span: "lg:col-span-3",
  },
];

export const MachineShowcase = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    if (!videoRef.current) return;
    const next = !muted;
    videoRef.current.muted = next;
    setMuted(next);
    if (!next) videoRef.current.play().catch(() => {});
  };

  return (
    <section
      id="floor"
      data-testid="floor-section"
      className="relative bg-[#FFFFFF] py-24 md:py-40 border-t border-[#C9C3B4]"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
          <Reveal>
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#0055FF]">
              / On our floor
            </span>
            <h2 className="font-display font-800 tracking-tighter text-3xl md:text-5xl lg:text-6xl mt-6 leading-[0.95] text-[#0F172A] max-w-2xl">
              TOYO precision. In-house capacity.
            </h2>
          </Reveal>
          <Reveal delay={0.08} className="max-w-sm md:text-right">
            <p className="text-[#55524B] leading-relaxed text-sm md:text-base">
              Two Japanese all-electric injection moulding machines — the
              <span className="text-[#0F172A]"> Si-180-6s</span> and
              <span className="text-[#0F172A]"> Si-280-7</span> — running
              precision cycles behind every unit we ship.
            </p>
          </Reveal>
        </div>

        {/* Row 1: video + 280 ton */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 mb-4 md:mb-5">
          <Reveal className="lg:col-span-7 relative overflow-hidden border border-[#C9C3B4] group bg-[#0F172A] min-h-[280px] md:min-h-[420px]">
            <video
              ref={videoRef}
              data-testid="floor-video"
              src="/factory/floor.mp4"
              poster="/factory/floor-poster.jpg"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#0055FF] animate-pulse" />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/90">
                Live from the floor
              </span>
            </div>
            <button
              data-testid="floor-video-sound"
              onClick={toggleSound}
              aria-label={muted ? "Unmute video" : "Mute video"}
              className="absolute top-4 right-4 z-10 btn-industrial bg-[#0F172A]/60 hover:bg-[#0055FF] text-white p-2.5 backdrop-blur-sm"
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <div className="absolute bottom-4 left-4 z-10">
              <div className="font-display font-800 tracking-tighter text-2xl md:text-3xl text-white">
                Si-280-7 · In cycle
              </div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/70 mt-1">
                280 Ton · All-Electric · Japanese Tech
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.06} className="lg:col-span-5 relative overflow-hidden border border-[#C9C3B4] group min-h-[280px] md:min-h-[420px]">
            <img
              src="/factory/si280.webp"
              alt="TOYO Si-280-7 injection moulding machine on the Tavas floor"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent" />
            <div className="absolute top-4 left-4 z-10 font-mono-tech text-[10px] uppercase tracking-widest bg-[#0055FF] text-white px-2.5 py-1">
              280 Ton
            </div>
            <div className="absolute bottom-4 left-4 z-10">
              <div className="font-display font-800 tracking-tighter text-xl md:text-2xl text-white">
                TOYO Si-280-7
              </div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/70 mt-1">
                Larger parts · Higher clamp force
              </div>
            </div>
          </Reveal>
        </div>

        {/* Row 2: automation video + machine stills */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5">
          <Reveal className="lg:col-span-5 relative overflow-hidden border border-[#C9C3B4] group bg-[#0F172A] min-h-[240px] md:min-h-[320px]">
            <video
              data-testid="floor-video-2"
              src="/factory/floor2.mp4"
              poster="/factory/floor2-poster.jpg"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#0055FF] animate-pulse" />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/90">
                Automated take-out
              </span>
            </div>
            <div className="absolute bottom-4 left-4 z-10">
              <div className="font-display font-800 tracking-tighter text-lg md:text-xl text-white">
                Robotic Pick &amp; Place
              </div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/70 mt-1">
                Mould → Conveyor · No human contact
              </div>
            </div>
          </Reveal>

          {machines.map((m, i) => (
            <Reveal
              key={m.key}
              delay={i * 0.06}
              className={`${m.span} relative overflow-hidden border border-[#C9C3B4] group min-h-[240px] md:min-h-[320px]`}
            >
              <img
                src={m.src}
                alt={m.label}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 z-10">
                <div className="font-display font-800 tracking-tighter text-lg md:text-xl text-white">
                  {m.label}
                </div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/70 mt-1">
                  {m.meta}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Spec strip */}
        <Reveal className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#C9C3B4] border border-[#C9C3B4]">
          {[
            ["2×", "TOYO Machines"],
            ["180 – 280T", "Clamping Range"],
            ["All-Electric", "Precision & Efficiency"],
            ["24 × 7", "Production Cycles"],
          ].map(([v, l]) => (
            <div key={l} className="bg-[#FFFFFF] p-6 md:p-8">
              <div className="font-display font-900 tracking-tighter text-2xl md:text-3xl text-[#0F172A]">
                {v}
              </div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#55524B] mt-2">
                {l}
              </div>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
};
